import { ShieldCheck, Route, Clock, Moon, Receipt } from "lucide-react";
import RouteMap from "./RouteMap";

const money = (n, cur = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(n || 0);

const mxn = (n, rate) =>
  rate ? new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format((n || 0) * rate) : null;

export default function EstimateResult({ route, pricing }) {
  if (!route || !pricing) return null;
  const cur = pricing.currency || "USD";
  const rate = pricing.exchange_rate;

  return (
    <div className="space-y-5" data-testid="estimate-result">
      {/* Safety-first banner */}
      <div className="rounded-xl bg-sage/10 border border-sage/30 p-4 flex gap-3">
        <ShieldCheck className="text-sage shrink-0 mt-0.5" size={20} />
        <p className="text-sm text-charcoal-700">
          <b>We choose the safest route, even if it takes longer.</b> Safer routes may increase travel
          time, overnight stops, tolls, and the final estimated price.
        </p>
      </div>

      {!route.geocode_ok && (
        <p className="text-sm text-terracotta-700 bg-terracotta-light/50 rounded-lg px-3 py-2">
          We couldn't precisely locate one of the places. The estimate below is approximate — an admin can refine it.
        </p>
      )}

      <RouteMap waypoints={route.waypoints} polyline={route.polyline} />

      {/* Route summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          [Route, "Distance", `${route.distance_km?.toLocaleString()} km`],
          [Clock, "Driving time", `${route.drive_hours} h`],
          [Moon, "Overnight stops", `${route.nights}`],
          [Receipt, "Est. tolls", money(route.toll_estimate, cur)],
        ].map(([Icon, label, val]) => (
          <div key={label} className="rounded-xl border border-sand-200 bg-white p-3">
            <Icon size={16} className="text-terracotta mb-1.5" />
            <div className="text-xs text-charcoal-500">{label}</div>
            <div className="text-base font-semibold text-charcoal-800">{val}</div>
          </div>
        ))}
      </div>

      {/* Pricing breakdown */}
      <div className="rounded-xl border border-sand-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-sand-200 bg-sand-50">
          <h4 className="font-medium text-charcoal-800">Estimate breakdown</h4>
        </div>
        <table className="w-full text-sm">
          <tbody>
            {pricing.line_items.map((li) => (
              <tr key={li.key} data-testid={`line-${li.key}`} className="border-b border-sand-100 last:border-0">
                <td className="px-4 py-3">
                  <div className="font-medium text-charcoal-800">{li.label}</div>
                  {li.detail && <div className="text-xs text-charcoal-500">{li.detail}</div>}
                </td>
                <td className="px-4 py-3 text-right font-semibold text-charcoal-800 whitespace-nowrap">
                  {money(li.amount, cur)}
                  {rate && <span className="block text-xs font-normal text-charcoal-400">{mxn(li.amount, rate)}</span>}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-sand-50">
              <td className="px-4 py-3 font-serif text-lg text-charcoal-800">Total estimate</td>
              <td data-testid="estimate-total" className="px-4 py-3 text-right font-serif text-lg text-terracotta whitespace-nowrap">
                {money(pricing.total, cur)}
                {rate && <span className="block text-sm font-sans text-charcoal-500">{mxn(pricing.total, rate)}</span>}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Alternatives */}
      {route.alternatives?.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wide text-charcoal-400 mb-2">Other routes we considered (not selected)</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {route.alternatives.map((alt) => (
              <div key={alt.label} className="rounded-lg border border-sand-200 bg-white p-3">
                <div className="text-sm font-medium text-charcoal-700">{alt.label}</div>
                <div className="text-xs text-charcoal-500 mt-0.5">
                  {alt.distance_km?.toLocaleString()} km · {alt.drive_hours} h · tolls {money(alt.toll_estimate, cur)}
                </div>
                <div className="text-xs text-charcoal-400 mt-1">{alt.note}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
